// CrossReg Nexus — Compliance Team Training (12 courses)
// Original course material © CrossReg Nexus Consultants LLC. Proprietary and confidential.
//
// Shared by the training app (viewer, dashboards) and the API (catalogue, certificates).

import { T1, T2 } from './team/t1-2.js';
import { T3, T4 } from './team/t3-4.js';
import { T5, T6 } from './team/t5-6.js';
import { T7, T8 } from './team/t7-8.js';
import { T9, T10 } from './team/t9-10.js';
import { T11, T12 } from './team/t11-12.js';

const SOURCE = [T1, T2, T3, T4, T5, T6, T7, T8, T9, T10, T11, T12];

export const TEAM_COURSE_PASS_MARK = 80;
export const TEAM_COURSE_COPYRIGHT = '© CrossReg Nexus Consultants LLC. Proprietary course material — not for redistribution.';

/** Catalogue order and ids of the Compliance Team Track. */
export const TEAM_COURSE_IDS = SOURCE.map(c => c.id);

/** Lightweight catalogue entries (title, description, number). */
export const TEAM_COURSE_CATALOG = SOURCE.map(c => ({
  id: c.id, number: c.number, title: c.title, description: c.description, domain: c.domain,
  slideCount: c.slides.length + 2, questionCount: c.quiz.length, labCount: c.handsOn.tasks.length,
}));

// Same deterministic answer shuffle as the CAMS course, so answer keys stay stable between visits.
function seeded(seed) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
function shuffleQuestion(q, seed) {
  const rnd = seeded(seed);
  const order = q.options.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; }
  return { ...q, options: order.map(i => q.options[i]), correctAnswer: order.indexOf(q.correctAnswer) };
}

/** Courses in the shape the training viewer renders: content slides, key facts, then the quiz slide. */
export const TEAM_COURSE_MODULES = Object.fromEntries(SOURCE.map(c => [c.id, {
  id: c.id,
  number: c.number,
  domain: c.domain,
  title: c.title,
  description: c.description,
  videoUrl: c.videoUrl,
  track: 'compliance-team',
  icon: '📘',
  slides: [
    ...c.slides.map((s, i) => ({ slideNumber: i + 1, ...s })),
    {
      slideNumber: c.slides.length + 1,
      title: `Course ${c.number} Key Facts`,
      subtitle: 'Review before the knowledge check',
      content: `Every point below is covered in the knowledge check. ${TEAM_COURSE_COPYRIGHT}`,
      keyPoints: c.keyFacts,
    },
    {
      slideNumber: c.slides.length + 2,
      title: `Course ${c.number} Knowledge Check`,
      content: `Ten questions on ${c.title}. You need ${TEAM_COURSE_PASS_MARK}% to pass; you can retake it.`,
      isQuiz: true,
      questions: c.quiz.map((q, i) => ({ id: i + 1, ...shuffleQuestion(q, 5000 + c.number * 1000 + i * 37 + 11) })),
    },
  ],
}]));

/** Hands-on CrossRegGuard labs, keyed by course id. */
export const TEAM_COURSE_HANDS_ON = Object.fromEntries(SOURCE.map(c => [c.id, c.handsOn]));
